import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Box,
	Avatar,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Tabs,
	Tab,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { PageError, TypedLottieSpinner } from "pymble-ui/components";
import { PastoralNote, PastoralFilters, usePastoralNotesInfinite, usePastoralNote, usePastoralCategories, usePastoralNotesExport } from "../../hooks/usePastoralNotes";
import NoteStatusChip from "./NoteStatusChip";
import { useProfileImageSas, buildProfileImageUrl } from "../../hooks/useProfileImageSas";
import { useContactDetails } from "../../hooks/useContactDetails";
import { useMyDetails } from "../../hooks/useMyDetails";
import FilterBar from "./FilterBar";
import PastoralNoteDrawer from "./PastoralNoteDrawer";

const FETCH_SIZE = 16;
const DISPLAY_STEP = 8;


function stripHtml(html: string): string {
	const div = document.createElement("div");
	div.innerHTML = html;
	return div.textContent ?? div.innerText ?? "";
}

const NoteRow: React.FC<{
	note: PastoralNote;
	photoUrl: string;
	isSelected: boolean;
	onClick: () => void;
}> = ({ note, photoUrl, isSelected, onClick }) => {

	// Coloured left border, matching the My Pymble portal note cards.
	// Child Protection (red) takes precedence over Wellbeing Confidential (amber).
	const isChildProtection = note.tags.some((t) => t.id === "ChildProtection");
	const isWellbeingConfidential = note.pastoral_type === "Wellbeing Confidential";
	const accentBorder = isChildProtection
		? "5px solid #d32f2f"
		: isWellbeingConfidential
			? "5px solid #ffc409"
			: undefined;

	return (
		<tr
			className="pa-grid-row"
			onClick={onClick}
			style={{ cursor: "pointer", backgroundColor: isSelected ? "#eef2fb" : undefined }}
		>
			{/* Student */}
			<td className="pa-grid-cell" style={{ borderLeft: accentBorder }}>
				<Box display="flex" alignItems="center" gap={1.5}>
					<Box sx={{ flexShrink: 0 }}>
						<Avatar
							src={photoUrl || undefined}
							sx={{ width: 36, height: 36, bgcolor: "#002f67", fontSize: 14 }}
						>
							{note.student_name.charAt(0)}
						</Avatar>
					</Box>
					<Box minWidth={0}>
						<Typography fontSize={14} fontWeight={500} lineHeight={1.3} noWrap>
							{note.student_name}
						</Typography>
						<Typography fontSize={12} color="text.secondary" noWrap>
							{note.student_year}
						</Typography>
					</Box>
				</Box>
			</td>
			{/* Date + Type */}
			<td className="pa-grid-cell">
				<Typography fontSize={13} color="text.secondary">
					{new Date(note.date).toLocaleDateString("en-AU", {
						day: "2-digit",
						month: "2-digit",
						year: "2-digit",
					})}
				</Typography>
				<Typography fontSize={12} fontWeight={500} color="text.secondary" noWrap>
					{note.pastoral_type}
				</Typography>
				{/* Status shown for child-protection notes only (Created/Viewed/Actioned),
				    matching the portal's student-context behaviour. */}
				{isChildProtection && note.raw_status && (
					<Box mt={0.5}>
						<NoteStatusChip status={note.raw_status} />
					</Box>
				)}
			</td>
			{/* Author */}
			<td className="pa-grid-cell">
				<Typography fontSize={13} color="text.secondary" noWrap>
					{note.author ?? "—"}
				</Typography>
			</td>
			{/* Summary */}
			<td className="pa-grid-cell" style={{ width: "100%", verticalAlign: "top" }}>
				<Typography
					fontSize={13}
					color="text.secondary"
					sx={{
						whiteSpace: "normal",
						wordBreak: "break-word",
						lineHeight: 1.4,
						display: "-webkit-box",
						WebkitLineClamp: 3,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}
				>
					{note.note ? stripHtml(note.note) : "—"}
				</Typography>
			</td>
			{/* Counts */}
			<td className="pa-grid-cell" style={{ width: 1, whiteSpace: "nowrap" }}>
				<Box display="flex" alignItems="center" gap={0.75}>
					{note.tags.length > 0 && (
						<Box display="flex" alignItems="center" gap={0.4}>
							<LocalOfferOutlinedIcon sx={{ fontSize: 13, color: "text.disabled" }} />
							<Typography fontSize={12} color="text.secondary">{note.tags.length}</Typography>
						</Box>
					)}
					{(note.comment_count ?? 0) > 0 && (
						<Box display="flex" alignItems="center" gap={0.4}>
							<ChatBubbleOutlineIcon sx={{ fontSize: 13, color: "text.disabled" }} />
							<Typography fontSize={12} color="text.secondary">{note.comment_count}</Typography>
						</Box>
					)}
					{note.files.length > 0 && (
						<Box display="flex" alignItems="center" gap={0.4}>
							<AttachFileIcon sx={{ fontSize: 13, color: "text.disabled" }} />
							<Typography fontSize={12} color="text.secondary">{note.files.length}</Typography>
						</Box>
					)}
				</Box>
			</td>
		</tr>
	);
};


const PastoralGrid: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	// Deep-linked student id (?student=) is used only to look up the student's
	// name and prefill the search box — it does not scope the grid to a pill.
	const initialStudentIdRef = useRef<string | null>(searchParams.get("student"));
	const [searchPrefilled, setSearchPrefilled] = useState(false);
	// Default to all dates — no date bound is applied until the user picks a preset.
	const [filters, setFilters] = useState<PastoralFilters>({});
	const [pendingNoteId, setPendingNoteId] = useState<string | null>(() => searchParams.get("note"));
	const [debouncedSearch, setDebouncedSearch] = useState<string | undefined>(undefined);
	const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const sentinelRef = useRef<HTMLTableRowElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const outerRef = useRef<HTMLDivElement>(null);
	const [scrollMaxHeight, setScrollMaxHeight] = useState("calc(100vh - 344px)");
	const [selectedNote, setSelectedNote] = useState<PastoralNote | null>(null);
	const [addStudent, setAddStudent] = useState<{ id: string; name: string } | null>(null);
	const [confirmExportOpen, setConfirmExportOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const { fetchAll: fetchAllForExport, fetchCommentsByNote } = usePastoralNotesExport();
	const [displayCount, setDisplayCount] = useState(DISPLAY_STEP);

	const { data: myDetails } = useMyDetails();
	const { data: categories } = usePastoralCategories(myDetails?._id);
	const { data: sasData } = useProfileImageSas();

	useEffect(() => {
		return () => {
			if (searchTimer.current) clearTimeout(searchTimer.current);
		};
	}, []);

	const {
		data,
		isPending,
		isError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = usePastoralNotesInfinite({
		...filters,
		search: debouncedSearch,
		limit: FETCH_SIZE,
	});

	// Recompute the scroll area height whenever the grid (re)mounts after a
	// load, since the grid DOM is absent while the full-screen loader shows.
	useEffect(() => {
		const compute = () => {
			if (!scrollRef.current) return;
			const top = scrollRef.current.getBoundingClientRect().top;
			setScrollMaxHeight(`${Math.max(200, window.innerHeight - top - 48)}px`);
		};
		compute();
		const ro = new ResizeObserver(compute);
		if (outerRef.current) ro.observe(outerRef.current);
		window.addEventListener("resize", compute);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", compute);
		};
	}, [isPending]);

	useEffect(() => {
		setDisplayCount(DISPLAY_STEP);
	}, [filters, debouncedSearch]);

	const allNotes = data?.pages.flatMap((p) => p.notes) ?? [];
	const visibleNotes = allNotes.slice(0, displayCount);
	const totalRecords = data?.pages[0]?.meta.total ?? 0;

	// Resolve a deep-linked note (?note=): prefer the copy already in the feed,
	// and fall back to a direct fetch for bare links whose note isn't loaded.
	const foundPendingNote = pendingNoteId ? allNotes.find((n) => n.id === pendingNoteId) : undefined;
	const { data: fetchedPendingNote } = usePastoralNote(
		pendingNoteId && !foundPendingNote && !isPending ? pendingNoteId : undefined,
	);

	useEffect(() => {
		if (!pendingNoteId) return;
		const resolved = foundPendingNote ?? fetchedPendingNote;
		if (resolved) {
			setSelectedNote(resolved);
			setPendingNoteId(null);
		}
	}, [pendingNoteId, foundPendingNote, fetchedPendingNote]);

	// Resolve the student's name/year from contact details: for the deep-linked
	// student (to prefill the search box) and to fill a resolved note whose feed
	// copy is missing its `About` (the single-note fetch isn't enriched).
	const enrichStudentId =
		initialStudentIdRef.current ??
		(selectedNote && (!selectedNote.student_name || selectedNote.student_name === "Unknown")
			? selectedNote.student_id
			: undefined);
	const { data: contactDetails } = useContactDetails(enrichStudentId || undefined);

	// When arriving via a note URL, drop the student's name into the search box
	// (filtering the feed by name) rather than showing a scope pill. Runs once.
	useEffect(() => {
		if (searchPrefilled) return;
		if (!initialStudentIdRef.current) {
			setSearchPrefilled(true);
			return;
		}
		if (contactDetails?.name && contactDetails.id === initialStudentIdRef.current) {
			setFilters({ search: contactDetails.name, date_from: "all_time" });
			setDebouncedSearch(contactDetails.name);
			setSearchPrefilled(true);
		}
	}, [contactDetails, searchPrefilled]);

	// Fill in a note's student name/year from contact details when the feed copy
	// (or single-note fetch) is missing it.
	const enrichNote = (n: PastoralNote | null): PastoralNote | null => {
		if (!n) return n;
		const hasName = !!n.student_name && n.student_name !== "Unknown";
		if (!hasName && contactDetails && contactDetails.id === n.student_id) {
			return { ...n, student_name: contactDetails.name || n.student_name, student_year: n.student_year || contactDetails.year || "" };
		}
		return n;
	};

	const displayNote = enrichNote(selectedNote);

	// Target for the "Add Note" button — only when we arrived via a student link.
	const deepLinkedStudent =
		initialStudentIdRef.current && contactDetails?.name && contactDetails.id === initialStudentIdRef.current
			? { id: initialStudentIdRef.current, name: contactDetails.name }
			: null;

	// Mirror the open note into the URL (opaque id, no PII); leave ?student as-is.
	useEffect(() => {
		const next = new URLSearchParams(searchParams);
		if (selectedNote) next.set("note", selectedNote.id);
		else if (!pendingNoteId) next.delete("note");
		if (next.toString() !== searchParams.toString()) {
			setSearchParams(next, { replace: true });
		}
	}, [selectedNote, pendingNoteId, searchParams, setSearchParams]);

	const handleSentinel = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			if (!entries[0].isIntersecting) return;
			if (allNotes.length === 0) return;

			if (displayCount < allNotes.length) {
				setDisplayCount((c) => c + DISPLAY_STEP);
			} else if (hasNextPage && !isFetchingNextPage) {
				fetchNextPage().then(() => {
					setDisplayCount((c) => c + DISPLAY_STEP);
				});
			}
		},
		[displayCount, allNotes.length, hasNextPage, isFetchingNextPage, fetchNextPage],
	);

	useEffect(() => {
		const el = sentinelRef.current;
		const root = scrollRef.current;
		if (!el || !root) return;
		const observer = new IntersectionObserver(handleSentinel, { root, threshold: 0.1 });
		observer.observe(el);
		return () => observer.disconnect();
	}, [handleSentinel]);

	const handleFilterChange = (updated: PastoralFilters) => {
		setFilters(updated);
		if (searchTimer.current) clearTimeout(searchTimer.current);
		searchTimer.current = setTimeout(() => setDebouncedSearch(updated.search), 400);
	};

	const handleDownload = () => {
		if (totalRecords > 300) {
			setConfirmExportOpen(true);
			return;
		}
		runExport();
	};

	const runExport = async () => {
		setIsExporting(true);
		try {
		const exportNotes: PastoralNote[] = await fetchAllForExport(filters, totalRecords);
		const commentMap = await fetchCommentsByNote(exportNotes.map((n) => n.id));

		// Find max number of comments across all notes to size dynamic columns
		let maxComments = 0;
		exportNotes.forEach((n) => {
			const count = commentMap.get(n.id)?.length ?? 0;
			if (count > maxComments) maxComments = count;
		});

		const ExcelJS = (await import("exceljs")).default;
		const wb = new ExcelJS.Workbook();
		const ws = wb.addWorksheet("Pastoral Notes");

		const baseColumns = [
			{ header: "Student", key: "student", width: 28 },
			{ header: "Year", key: "year", width: 12 },
			{ header: "Date", key: "date", width: 14, style: { numFmt: "dd/mm/yyyy" } },
			{ header: "Author", key: "author", width: 24 },
			{ header: "Type", key: "type", width: 18 },
			{ header: "Tags", key: "tags", width: 30 },
			{ header: "Status", key: "status", width: 14 },
			{ header: "Note", key: "note", width: 60 },
			{ header: "Attachments", key: "attachments", width: 14, style: { numFmt: "0" } },
		];

		const commentColumns = Array.from({ length: maxComments }, (_, i) => ({
			header: `Comment ${i + 1}`,
			key: `comment_${i + 1}`,
			width: 60,
		}));

		ws.columns = [...baseColumns, ...commentColumns];

		// Style header row
		const headerRow = ws.getRow(1);
		headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
		headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF002F67" } };
		headerRow.alignment = { vertical: "middle" };
		headerRow.height = 20;

		const STATUS_MAP: Record<string, string> = { active: "Active", monitoring: "Monitoring", resolved: "Resolved" };

		exportNotes.forEach((n: PastoralNote) => {
			const comments = commentMap.get(n.id) ?? [];
			const commentCells: Record<string, string> = {};
			comments.forEach((c, i) => {
				const date = c.date ? new Date(c.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "";
				commentCells[`comment_${i + 1}`] = `${c.author} (${date}): ${c.text}`;
			});

			ws.addRow({
				student: n.student_name,
				year: n.student_year,
				date: n.date ? new Date(n.date) : "",
				author: n.author ?? "",
				type: n.pastoral_type,
				tags: n.tags.map((t: { name: string }) => t.name).join("; "),
				status: STATUS_MAP[n.status] ?? n.status,
				note: stripHtml(n.note ?? ""),
				attachments: n.files.length,
				...commentCells,
			});
		});

		// Freeze header and auto-filter over base columns only
		const lastCol = ws.getColumn(baseColumns.length).letter;
		ws.views = [{ state: "frozen", ySplit: 1 }];
		ws.autoFilter = { from: "A1", to: `${lastCol}1` };

		const buffer = await wb.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `pastoral-notes-${new Date().toISOString().split("T")[0]}.xlsx`;
		a.click();
		URL.revokeObjectURL(url);
		} finally {
			setIsExporting(false);
		}
	};

	// Show the loader inside the grid body (full width) while keeping the tab +
	// filter chrome visible, on any load — initial mount and every filter/search
	// change. Errors with no data render in the same spot.
	const showLoadingState = isPending || (isError && allNotes.length === 0);

	return (
		<Box ref={outerRef}>
			{/* Tab + FilterBar — full width above grid and drawer */}
			<Box sx={{ position: "relative", display: "flex", alignItems: "center", borderBottom: "1px solid #e8ecf0" }}>
				<Tabs
					value={0}
					sx={{
						minHeight: 44,
						"& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: 14, minHeight: 44, py: 0 },
						"& .MuiTabs-indicator": { bgcolor: "#c8102e" },
						"& .Mui-selected": { color: "#002f67 !important" },
					}}
				>
					<Tab label="Grid" />
				</Tabs>
				{/* FilterBar centered relative to the full row */}
				<Box sx={{ position: "absolute", left: 0, right: 0, display: "flex", justifyContent: "center", pointerEvents: "none", py: 0.75 }}>
					<Box sx={{ pointerEvents: "auto" }}>
						<FilterBar
							filters={filters}
							onChange={handleFilterChange}
							categories={categories}
							showSearch
							showDownload
							onDownload={handleDownload}
							isExporting={isExporting}
							drawerOpen={!!selectedNote}
							onAddNote={
								deepLinkedStudent
									? () => {
										setSelectedNote(null);
										setAddStudent(deepLinkedStudent);
									}
									: undefined
							}
						/>
					</Box>
				</Box>
			</Box>

			{/* Grid + Drawer */}
			<Box display="flex" alignItems="stretch" sx={{ height: scrollMaxHeight, overflow: "hidden" }}>
				<Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
					<Box
						ref={scrollRef}
						sx={{ overflowX: "auto", overflowY: "auto", flex: 1, minHeight: 200 }}
					>
					{showLoadingState ? (
						<Box
							sx={{
								width: "100%",
								minHeight: "100%",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								py: 8,
							}}
						>
							{isError ? (
								<PageError error="Failed to load pastoral notes." />
							) : (
								<TypedLottieSpinner type="General" label="Loading pastoral notes..." size={160} />
							)}
						</Box>
					) : (
					<>
					{isError && <PageError error="Failed to load pastoral notes." />}
					<table className="pa-grid-table">
						<thead>
							<tr>
								<th className="pa-grid-th">Student</th>
								<th className="pa-grid-th">Date</th>
								<th className="pa-grid-th">Author</th>
								<th className="pa-grid-th">Note</th>
								<th className="pa-grid-th" style={{ width: 1, whiteSpace: "nowrap" }}></th>
							</tr>
						</thead>
						<tbody>
							{visibleNotes.map((note) => {
								const rowNote = enrichNote(note) as PastoralNote;
								return (
									<NoteRow
										key={rowNote.id}
										note={rowNote}
										photoUrl={buildProfileImageUrl(rowNote.student_id, sasData)}
										isSelected={selectedNote?.id === rowNote.id}
										onClick={() => setSelectedNote((prev) => (prev?.id === rowNote.id ? null : rowNote))}
									/>
								);
							})}
							<tr ref={sentinelRef} style={{ height: 1 }} />
						</tbody>
					</table>
					{isFetchingNextPage && (
						<Box display="flex" justifyContent="center" py={1.5}>
							<CircularProgress size={18} thickness={5} />
						</Box>
					)}
					{visibleNotes.length === 0 && (
						<Box textAlign="center" py={6}>
							<Typography color="text.secondary">No pastoral notes found.</Typography>
						</Box>
					)}
					</>
					)}
					</Box>
				</Box>

				{/* Drawer */}
				<PastoralNoteDrawer
					note={displayNote}
					onClose={() => setSelectedNote(null)}
					isAdding={!!addStudent}
					studentId={addStudent?.id}
					studentName={addStudent?.name}
					onAddClose={() => setAddStudent(null)}
				/>
			</Box>

			{/* Export confirmation */}
			<Dialog open={confirmExportOpen} onClose={() => setConfirmExportOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Large export</DialogTitle>
				<DialogContent>
					<Typography fontSize={14}>
						You are about to export <strong>{totalRecords.toLocaleString()}</strong> records. This may take a moment. Do you want to continue?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmExportOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
					<Button
						variant="contained"
						onClick={() => { setConfirmExportOpen(false); runExport(); }}
						sx={{ textTransform: "none" }}
					>
						Export
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PastoralGrid;
